import { Link, Stack } from "@mui/material";
import { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPenLine, faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import {
	useDeleteCompaniesMutation,
	useGetCompaniesQuery,
} from "@/pages/Private/redux/companies/companies.api";

import { Icon, LoadingOverlay, PermissionCheck, TableColumn, TableComponent } from "@/components";
import { useSearch } from "@/utils";

import { PermissionRoles } from "@/enum";

import DeleteModal from "@/components/Modals/DeleteModal";

import { Companies } from "../schema/companies";
import { UpdateCompaniesModal } from "./UpdateCompaniesModal";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CompaniesTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string, options = {}) => t(`companies.${key}`, options), [t]);
	const [showEditModal, setShowEditModal] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [selectedCompanies, setSelectedCompanies] = useState<Companies | null>(null);
	const { page, setPage, limit, setLimit, query } = useSearch();
	const [deleteCompanies, { isLoading: isDeleteLoading }] = useDeleteCompaniesMutation();

	const { data, isLoading } = useGetCompaniesQuery({
		page,
		limit,
		searchValue: query,
	});

	const columns: TableColumn<Companies>[] = useMemo(
		() => [
			{
				sx: { "border-bottom": "0px", padding: "0px", width: "100%" },
				label: ts("name"),
				format: (row) => (
					<Stack alignItems={"flex-start"} direction="row" justifyContent="flex-start">
						<Link href={`/app/companies/${row?.id}`}>
							<Stack
								alignItems="center"
								direction="column"
								display="flex"
								flexDirection="row"
								justifyContent="flex-start"
							>
								<p className="w-[44px] h-[44px] text-[14px] flex items-center justify-center text-white rounded-xl bg-primary-blue-dark leading-5">
									{row?.name[0]}
								</p>
								<Stack direction="column" justifyContent="flex-start" ml={2}>
									<p className="text-[14px] font-medium leading-5 text-primary-blue-dark">
										{row.name}
									</p>

									{row?.website && (
										<p className="text-[12px] font-normal leading-4 text-primary-blue">
											{row.website}
										</p>
									)}
								</Stack>
							</Stack>
						</Link>
					</Stack>
				),
			},
			{
				sx: { "border-bottom": "0px", padding: "0px" },
				align: "center",
				label: "",
				minWidth: 20,
				format: (row) => (
					<div className="flex gap-2 !w-fit !ml-auto items-center">
						<PermissionCheck requiredPermissions={[PermissionRoles.COOKIE]}>
							<>
								<button
									className="w-[44px] h-[44px] flex items-center justify-center rounded-xl border-[0.28px] border-[#E6E6EC]"
									onClick={() => handleEditCompanies(row)}
								>
									<Icon icon={faPenLine} size="lg" />
								</button>
								<button
									className="w-[44px] h-[44px] flex items-center justify-center rounded-xl border-[0.28px] border-[#E6E6EC]"
									onClick={() => handleDeleteModal(row)}
								>
									<Icon icon={faTrashCan} size="lg" />
								</button>
							</>
						</PermissionCheck>
					</div>
				),
			},
		],
		[ts]
	);

	const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
		setLimit(Number(event.target.value));
		setPage(1);
	};

	const handleEditCompanies = (row: Companies) => {
		setShowEditModal(true);
		setSelectedCompanies(row);
	};

	const handleDeleteModal = (row: Companies) => {
		setShowDeleteModal(true);
		setSelectedCompanies(row);
	};

	const handleDeleteCompanies = async () => {
		try {
			await deleteCompanies(selectedCompanies?.id ?? 0).unwrap();
			setShowDeleteModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	return !data && !isLoading ? (
		<LoadingOverlay />
	) : (
		<div className="w-full">
			<div className="w-full bg-white">
				<TableComponent
					bodySx={{
						overflowY: "auto",
						borderRadius: "16px",
					}}
					columns={columns}
					data={data?.data || []}
					handleChangeLimit={handleChangeRowsPerPage}
					handleChangePage={(_: unknown, page: number) => setPage(page + 1)}
					headerSx={{
						display: "block",
						paddingX: "8px",
						backgroundColor: "#F7F8FB",
					}}
					hideHeader={true}
					itemCount={data?.meta.itemCount ?? 0}
					limit={limit}
					page={page}
					rowSx={{
						display: "block",
						padding: "8px",
						borderRadius: "12px",
						boxShadow: "0px 1px 2px 0px #1C274C1F",
						border: "1px solid #E6E6EC",
						backgroundColor: "#FFFFFF",
						marginBottom: "12px",
					}}
					tableSx={{
						paddingX: "4px",
						paddingTop: "0px",
						borderRadius: "16px",
						width: "100%",
					}}
				/>
			</div>
			{showDeleteModal && (
				<DeleteModal
					description={ts("delete.description", { companyName: selectedCompanies?.name })}
					handleClose={() => setShowDeleteModal(false)}
					handleDelete={handleDeleteCompanies}
					isLoading={isDeleteLoading}
					isShowing={showDeleteModal}
					title={ts("delete.title")}
				/>
			)}
			{showEditModal && selectedCompanies && (
				<UpdateCompaniesModal
					companies={selectedCompanies}
					isOpen={showEditModal}
					setIsOpen={setShowEditModal}
				/>
			)}
		</div>
	);
};
